<template>
  <div class="comp-container comp-container__group POS-f TRBL-0">
    <div class="row no-gutters justify-content-center">
      <div class="col col-md-6 col-lg-4">
        <transition
          name="animation-slide-in-bottom"
        >
          <div
            class="modal P-m BDRS-tl-m BDRS-tr-m BGC-white"
            v-if="show"
          >
            <div class="header MB-m TA-c">
              <div class="header__left"></div>
              <div class="header__title FW-b">{{ $t('group.groupOrder') }}</div>
              <div class="header__right" v-on:click="close()">
                <button><span class="material-icons-round">close</span></button>
              </div>
            </div>
            <template v-if="currentGroup.id">
              <template v-if="currentOrder.id">
                <div class="main MB-l">
                  <div class="TA-c">
                    {{ $t('group.shareLink') }}
                  </div>
                  <textarea
                    id="send-text"
                    class="send-text W-100 MT-m PT-s PR-m PB-s PL-m BDRS-m BGC-light FZ-14"
                    :class="{
                      'is-loading': groupLoading,
                    }"
                    aria-label="send-text"
                    :value="shareText + ' ' + shareURL"
                    rows="3"
                    readonly
                  />
                  <div class="send-tools">
                    <a class="send-btn CU-p" v-if="webShareAPI" v-on:click="shareViaWebShareAPI">
                      <div class="icon BGC-light BDRS-100">
                        <i class="material-icons-round">ios_share</i>
                      </div>
                      <div class="label FZ-14">{{ $t('group.share') }}</div>
                    </a>
                    <a
                      class="send-btn send-btn-copy CU-p"
                      data-clipboard-target="#send-text"
                      :aria-label="shareURL"
                      v-on:click="shareViaCopy"
                    >
                      <div class="icon BGC-light BDRS-100">
                        <i class="material-icons-round">file_copy</i>
                      </div>
                      <div class="label FZ-14">{{ $t('group.copy') }}</div>
                    </a>
                  </div>
                  <div class="group-tools MT-m TA-c">
                    <button
                      v-if="currentGroup.owner"
                      class="MR-xxs ML-xxs PT-xs PR-m PB-xs PL-m BDRS-99 BGC-light C-grey FZ-12 FW-b BUTTON-reset ACTIVE-small"
                      v-on:click="cancelGroup"
                      :is-loading="groupCanceling"
                    >
                      {{ $t('group.cancelGroup') }}
                    </button>
                    <button
                      class="MR-xxs ML-xxs PT-xs PR-m PB-xs PL-m BDRS-99 BGC-light C-grey FZ-12 FW-b BUTTON-reset ACTIVE-small"
                      v-on:click="initAnotherGroup"
                    >
                      {{ $t('group.createNewGroupOrder') }}
                    </button>
                  </div>
                </div>
                <div class="footer POS-s">
                  <yellow-button
                    :label="$t('group.start')"
                    v-on:click.native="close()"
                  ></yellow-button>
                </div>
              </template>
              <template v-else>
                <div class="main MB-xl">
                  <div class="group-fig no-avatar MT-xl MB-xl">
                    <div class="icon BGC-brand">
                      <span class="material-icons-round">group_add</span>
                    </div>
                  </div>
                  <div class="TA-c">
                    {{ $t('group.joinGroup{name}', { name: currentGroup.host }) }}
                  </div>
                  <custom-input
                    class="MT-l"
                    :label="$t('group.name')"
                    type="text"
                    v-bind="$attrs"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    v-model.trim="guestName"
                    v-if="guestMode"
                  />
                </div>
                <div
                  class="footer POS-s"
                  v-if="!guestMode"
                >
                  <template
                    v-if="user.logged_in"
                  >
                    <yellow-button
                      :label="$t('group.join')"
                      v-on:click.native="joinGroup"
                      :is-loading="groupLoading"
                    ></yellow-button>
                  </template>
                  <template v-else>
                    <yellow-button
                      :label="$t('group.guest')"
                      v-on:click.native="guestMode = true"
                    ></yellow-button>
                    <yellow-button
                      :label="$t('group.login')"
                      v-on:click.native="joinGroup"
                      :is-loading="groupLoading"
                    ></yellow-button>
                  </template>
                </div>
                <div
                  class="footer POS-s"
                  v-else
                >
                  <yellow-button
                    :label="$t('group.join')"
                    v-on:click.native="joinGroup"
                    :is-loading="groupLoading"
                  ></yellow-button>
                </div>
              </template>
            </template>

            <template v-else>
              <div class="main MB-xl">
                <div class="group-fig" :class="{ 'no-avatar': !user.avatar }">
                  <div class="user POS-r">
                    <img class="avatar POS-a TRBL-0 W-100 H-100" :src="user.avatar" alt="avatar">
                  </div>
                  <div class="icon BGC-brand">
                    <span class="material-icons-round">group_add</span>
                  </div>
                </div>
                <div class="MT-l TA-c">
                  {{ $t('group.groupOrderDesc') }}
                </div>
              </div>
              <div class="footer POS-s">
                <yellow-button
                  :label="$t('group.startGroup')"
                  :is-loading="groupLoading"
                  @click.native="initGroup()"
                ></yellow-button>
              </div>
            </template>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import ClipboardJS from 'clipboard';
import Axios from '@/helpers/axios';
import Swal from 'sweetalert2';

import yellowButton from '@/components/form/YellowButton.vue';
import customInput from '@/components/form/Input.vue';

import { mapState, mapMutations } from 'vuex';

export default {
  name: 'Group',
  props: {
    order: {
      type: String,
      default: '',
      require: true,
    },
    orderType: {
      type: String,
    },
    orderTime: {
      type: String,
    },
    isGroupOrders: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    yellowButton,
    customInput,
  },
  computed: {
    ...mapState([
      'user',
      'client',
      'store',
      'currentOrder',
      'currentGroup',
    ]),
    loginAndRedirectBackURL() {
      let loginUrl = process.env.NODE_ENV === 'development'
        ? `http://localhost:8081/vip/${this.$route.params.brandId}/entry?`
        : `https://ocard.co/vip/${this.$route.params.brandId}/entry?`;

      loginUrl += `r=${encodeURIComponent(window.location.href)}`;
      loginUrl += '&via=order';
      if (this.client.ltk) {
        loginUrl += '&';
        loginUrl += `s=${this.client.ltk}`;
      }

      return loginUrl;
    },
    shareText() {
      if (!this.order && this.isGroupOrders) return null;

      return this.$i18n.t('group.join{name}', { name: this.currentGroup.host });
    },
    shareURL() {
      if (!this.order && this.isGroupOrders) return '';

      const store = this.$router.resolve({
        name: 'Store',
        params: { brandId: this.$route.params.brandId, storeId: this.$route.params.storeId },
        query: { group: this.currentGroup.id },
      });

      return window.location.origin + store.href;
    },
  },
  data() {
    return {
      show: false,
      groupLoading: false,
      groupCanceling: false,
      newGroupLoading: false,
      webShareAPI: navigator.share,
      guestMode: false,
      guestName: null,
      isReplacingGroup: false,
    };
  },
  mounted() {
    this.show = true;
  },
  methods: {
    ...mapMutations([
      'SET_CURRENT_ORDER',
      'RESET_CURRENT_ORDER',
      'SET_CURRENT_ORDER_DELIVERY',
      'SET_CURRENT_GROUP',
      'RESET_CURRENT_GROUP',
      'SET_CART',
      'REMOVE_FROM_SAVED_ORDERS',
      'RESET_CART',
    ]),
    close() {
      if (!this.isReplacingGroup) {
        this.$emit('close');
        return;
      }

      this.$emit('close');

      if (this.currentGroup?.id) {
        window.location.reload();
      } else {
        this.$router.push({
          name: 'Store',
          params: { brandId: this.$route.params.brandId, storeId: this.$route.params.storeId },
        });
        return;
      }

      if (!this.currentOrder?.id) {
        this.RESET_CURRENT_ORDER();
        this.RESET_CURRENT_GROUP();
        this.$router.push({ query: null });
        window.location.reload();
      }
    },
    close2() {
      this.$emit('close');
      if (this.currentGroup?.id) {
        window.location.reload();
      } else {
        this.$router.push({
          name: 'Store',
          params: { brandId: this.$route.params.brandId, storeId: this.$route.params.storeId },
        });
      }
    },
    initGroup(needRedirect = false) {
      if (!this.user.logged_in) {
        window.location.href = this.loginAndRedirectBackURL;
        return;
      }

      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        store_id: this.$route.params.storeId,
        orderType: this.orderType,
      };

      if (this.orderTime) {
        data.orderDateTime = this.orderTime;
      }

      if (!this.isGroupOrders) {
        data.order_id = this.currentOrder.id;
      }

      this.groupLoading = true;
      this.$emit('page-load', true);

      Axios({
        method: 'post',
        url: '/group/init',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  if (response.data.clearOrder) {
                    this.REMOVE_FROM_SAVED_ORDERS({
                      brand: this.$route.params.brandId,
                      store: this.$route.params.storeId,
                    });
                    this.RESET_CART();
                  }

                  if (response.data.reload) {
                    window.location.reload();
                  }

                  if (response.data.redirect) {
                    this.$router.push(response.data.redirect);
                  }
                });
            } else {
              if (response.data.clearOrder) {
                this.REMOVE_FROM_SAVED_ORDERS({
                  brand: this.$route.params.brandId,
                  store: this.$route.params.storeId,
                });
                this.RESET_CART();
              }

              if (response.data.reload) {
                window.location.reload();
              }

              if (response.data.redirect) {
                this.$router.push(response.data.redirect);
              }
            }
            this.groupLoading = false;
            this.$emit('page-load', false);
            return;
          }

          if (response.data.code === 500) {
            this.SET_CURRENT_ORDER({
              brand: this.$route.params.brandId,
              store: this.$route.params.storeId,
              id: response.data.data.order.id,
              property: response.data.data.order.property || null,
            });

            this.SET_CART(response.data.data.order);

            this.SET_CURRENT_GROUP({
              brand: this.$route.params.brandId,
              store: this.$route.params.storeId,
              id: response.data.data.group.id,
              host: response.data.data.group.host.name,
              owner: response.data.data.group.owner,
            });

            if (needRedirect) {
              this.$router.push({
                name: 'Store',
                params: {
                  brandId: this.$route.params.brandId,
                  storeId: this.$route.params.storeId,
                },
                query: {
                  group: response.data.data.group.id,
                  showGroup: true,
                },
              });
            }

            this.$root.$message.success(this.$i18n.t('group.initSuccess', { orderId: response.data.data.group.id }));

            this.groupLoading = false;

            if (!this.isGroupOrders) {
              this.isReplacingGroup = true;
            }
          }
        })
        .catch(() => {
          this.groupLoading = false;
          this.$emit('page-load', false);
        });
    },
    initAnotherGroup() {
      Swal.fire({
        text: this.$i18n.t('group.initNewGroup'),
        showCancelButton: true,
        cancelButtonText: this.$i18n.t('common.cancel'),
        confirmButtonText: this.$i18n.t('common.ok'),
        reverseButtons: true,
      })
        .then(async (result) => {
          if (result.value === true) {
            await this.initGroup(true);
          }
        });
    },
    shareViaCopy() {
      const clipboard = new ClipboardJS('.send-btn-copy', {
        text: () => `${this.shareText} ${this.shareURL} `,
      });

      clipboard.on('success', () => {
        this.$root.$message.success(this.$i18n.t('group.copied'));
      });
    },
    shareViaWebShareAPI() {
      navigator
        .share({
          text: this.shareText,
          url: this.shareURL,
        });
    },
    joinGroup() {
      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        store_id: this.$route.params.storeId,
        group_id: this.currentGroup.id,
      };

      if (this.guestMode) {
        data.name = this.guestName;
      } else if (!this.user.logged_in) {
        window.location.href = this.loginAndRedirectBackURL;
        return;
      }

      this.groupLoading = true;

      Axios({
        method: 'post',
        url: '/group/join',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  if (response.data.clearOrder) {
                    this.REMOVE_FROM_SAVED_ORDERS({
                      brand: this.$route.params.brandId,
                      store: this.$route.params.storeId,
                    });
                    this.RESET_CART();
                  }

                  if (response.data.reload) {
                    window.location.reload();
                  }

                  if (response.data.redirect) {
                    this.$router.push(response.data.redirect);
                  }
                });
            } else {
              if (response.data.clearOrder) {
                this.REMOVE_FROM_SAVED_ORDERS({
                  brand: this.$route.params.brandId,
                  store: this.$route.params.storeId,
                });
                this.RESET_CART();
              }

              if (response.data.reload) {
                window.location.reload();
              }

              if (response.data.redirect) {
                this.$router.push(response.data.redirect);
              }
            }

            this.groupLoading = false;
            return;
          }

          if (response.data.code === 500) {
            this.SET_CURRENT_ORDER({
              brand: this.$route.params.brandId,
              store: this.$route.params.storeId,
              id: response.data.data.order.id,
              property: response.data.data.order.property || null,
            });

            this.SET_CART(response.data.data.order);

            this.SET_CURRENT_GROUP({
              brand: this.$route.params.brandId,
              store: this.$route.params.storeId,
              id: response.data.data.group.id,
              host: response.data.data.group.host.name,
              owner: response.data.data.group.owner,
            });

            this.groupLoading = false;
          }
        });
    },
    cancelGroup() {
      Swal.fire({
        text: this.$i18n.t('group.cancelOrderWarnText'),
        showCancelButton: true,
        cancelButtonText: this.$i18n.t('group.cancelOrder'),
        confirmButtonText: this.$i18n.t('group.continue'),
        reverseButtons: true,
      })
        .then((result) => {
          if (result.value === undefined) {
            const data = {
              ...this.$route.query,
              group_id: this.currentGroup.id,
            };

            this.groupCanceling = true;

            Axios({
              method: 'post',
              url: '/group/cancel',
              data,
            })
              .then((response) => {
                if (response.data.code !== 500) {
                  if (response.data.msg) {
                    Swal.fire({
                      text: response.data.msg,
                    })
                      .then(() => {
                        if (response.data.clearOrder) {
                          this.REMOVE_FROM_SAVED_ORDERS({
                            brand: this.$route.params.brandId,
                            store: this.$route.params.storeId,
                          });
                          this.RESET_CART();
                        }

                        if (response.data.reload) {
                          window.location.reload();
                        }

                        if (response.data.redirect) {
                          this.$router.push(response.data.redirect);
                        }
                      });
                  } else {
                    if (response.data.clearOrder) {
                      this.REMOVE_FROM_SAVED_ORDERS({
                        brand: this.$route.params.brandId,
                        store: this.$route.params.storeId,
                      });
                      this.RESET_CART();
                    }

                    if (response.data.reload) {
                      window.location.reload();
                    }

                    if (response.data.redirect) {
                      this.$router.push(response.data.redirect);
                    }
                  }

                  this.groupLoading = false;
                  return;
                }

                if (response.data.code === 500) {
                  this.REMOVE_FROM_SAVED_ORDERS({
                    brand: this.$route.params.brandId,
                    store: this.$route.params.storeId,
                  });
                  this.RESET_CART();

                  this.groupCanceling = false;
                  this.close();
                }
              });
          }
        });
    },
  },
};
</script>

<style lang="sass" scoped>
.comp-container__group
  width: 100%
  z-index: $z-index-modal
  overflow: auto
  display: flex
  flex-direction: column
  justify-content: flex-end

  background-color: rgba(0, 0, 0, .25)

  user-select: none

  &::-webkit-scrollbar
    display: none

.modal
  bottom: 0
  display: flex
  flex-direction: column

.header
  display: flex
  align-items: center

  &__left,
  &__title,
  &__right
    height: 32px

  &__left,
  &__right
    width: 32px

  &__title
    flex-grow: 1
    line-height: 32px

  button
    @extend .P-xxs
    @extend .W-xl
    @extend .H-xl

    border: none
    @extend .BDRS-s

    @extend .BGC-t

    @extend .C-grey

    appearance: none
    outline: 0

    &:active
      @extend .BGC-light

      outline: 0

.main
  flex-grow: 1

.group-fig
  display: flex
  align-items: center
  justify-content: center

  &.no-avatar
    .user
      display: none

    .icon
      animation-name: rotate-y

  .user,
  .icon
    width: 104px
    height: 104px
    overflow: hidden

    border-radius: 100%

    animation-duration: 5s
    animation-iteration-count: infinite
    animation-timing-function: ease-in-out

  .user
    animation-name: left-to-right

    &::after
      @extend .POS-a
      @extend .TRBL-0

      @extend .BDRS-100
      border: 1px solid rgba(0, 0, 0, .1)

      content: ''

  .icon
    display: flex
    align-items: center
    justify-content: center

    animation-name: right-to-left

    .material-icons-round
      font-size: 56px

.send-text
  border: none

  word-break: break-all

  &.is-loading
    color: transparent

.send-tools
  margin: 10px -15px 0

  white-space: nowrap
  overflow-y: auto
  -webkit-overflow-scrolling: touch

  text-align: center

.send-btn
  margin: 0 20px 0 0

  display: inline-block
  text-align: center

  color: inherit
  text-decoration: none
  vertical-align: middle

  &:first-child
    margin-left: 20px

  &:hover
    color: currentcolor

  .icon
    display: inline-block
    width: 54px
    height: 54px
    overflow: hidden

    .line,
    .messenger
      margin-top: 14px
      height: 26px
      width: auto

  .material-icons-round
    width: 54px
    height: 54px

    line-height: 54px

.footer
  display: flex

  button
    flex: 1
    @extend .ML-xs
    @extend .MR-xs

    &:first-child
      margin-left: 0

    &:last-child
      margin-right: 0

@keyframes left-to-right
  0%,
  40%
    transform: translateX(4px)

  50%,
  90%
    transform: translateX(calc(100% - 4px))

  100%
    transform: translateX(4px)

@keyframes right-to-left
  0%,
  40%
    transform: translateX(-4px)

  50%,
  90%
    transform: translateX(calc(-100% + 4px))

  100%
    transform: translateX(-4px)

@keyframes rotate-y
  0%,
  40%
    transform: rotateY(0deg)

  50%,
  90%
    transform: rotateY(360deg)

  100%
    transform: rotateY(0deg)
</style>
